// src/components/ui/AssistantTestDock.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Textarea } from './textarea';
import { X, Phone, PhoneOff, Loader2, Volume2 } from 'lucide-react';
import { useVapiCall } from '@/hooks/useVapiCall';

interface AssistantTestDockProps {
  assistantId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultInstruction?: string;
}

export default function AssistantTestDock({ assistantId, open, onOpenChange, defaultInstruction }: AssistantTestDockProps) {
  const { isCalling, isConnecting, error, initiateCall, endCall } = useVapiCall();
  const [instruction, setInstruction] = useState<string>(
    defaultInstruction || 'Quick web test: greet warmly as the business, then ask how you can help with scheduling or questions.'
  );
  const panelRef = useRef<HTMLDivElement | null>(null);

  const canTest = useMemo(() => Boolean(assistantId), [assistantId]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus();
  }, [open]);

  const handleStart = async () => {
    if (!assistantId) return;
    const callContext = {
      selected_tool: 'manual_upload',
      custom_manual_instruction: instruction,
      command_id: 'settings_test',
      saas_call_type: 'Web Test Call'
    } as const;
    await initiateCall(assistantId, instruction, callContext, {});
  };

  return (
    <div>
      {/* Floating trigger when closed */}
      {!open && (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-label="Open Test AI dock"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center justify-center bg-indigo-600 text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full md:rounded-full md:h-12 md:w-12 h-12 w-auto px-4 md:px-0"
        >
          <span className="hidden md:inline-flex"><Phone className="h-5 w-5" /></span>
          <span className="md:hidden inline-flex items-center text-sm font-medium gap-2"><Phone className="h-4 w-4" /> Test AI</span>
        </button>
      )}

      {/* Dock panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Test your AI"
          ref={panelRef}
          tabIndex={-1}
          className="fixed bottom-6 right-6 z-50 w-[94vw] max-w-md"
        >
          <Card className="shadow-2xl border-indigo-200">
            <CardHeader className="pb-2 flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Volume2 className="h-4 w-4 text-indigo-600" /> Test Your AI
              </CardTitle>
              <button
                type="button"
                aria-label="Close"
                onClick={() => onOpenChange(false)}
                className="p-1 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </CardHeader>
            <CardContent className="space-y-3">
              {!canTest && (
                <div className="p-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs">
                  Finish setup and save your AI to enable testing.
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="assistant-test-instruction" className="text-xs font-medium text-slate-700">
                  What should your AI do in this test?
                </label>
                <Textarea
                  id="assistant-test-instruction"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  rows={3}
                  className="text-sm"
                  placeholder="Introduce yourself as the business and ask how you can help."
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={isCalling ? endCall : handleStart}
                  disabled={!canTest || isConnecting}
                >
                  {isConnecting && (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting…</>)}
                  {!isConnecting && !isCalling && (<><Phone className="mr-2 h-4 w-4" /> Start Test Call</>)}
                  {!isConnecting && isCalling && (<><PhoneOff className="mr-2 h-4 w-4" /> End Call</>)}
                </Button>
                {error && <span className="text-xs text-red-600">{error}</span>}
              </div>

              <div className="text-[11px] text-slate-500">
                Tip: Grant microphone permission when prompted. You can keep this dock open while adjusting settings.
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


